import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useChatAlerts } from "@/hooks/useChatAlerts";
import ChatSoundBanner from "@/components/ChatSoundBanner";
import { ChatCircleDots, Warning, LinkSimple, ArrowClockwise, Prohibit, PaperPlaneRight, ArrowLeft, CircleNotch, Camera, VideoCamera, Images, X, WhatsappLogo, Archive, ArrowUUpLeft } from "@phosphor-icons/react";

const SL = { menunggu: "Menunggu", siap_berangkat: "Siap Berangkat", dalam_perjalanan: "Dalam Perjalanan", tiba: "Sudah Tiba", selesai: "Layanan Selesai" };
const VID_MAX = 30 * 1024 * 1024;

// Normalisasi + validasi nomor Indonesia untuk wa.me (08.. -> 628..). Return URL atau null.
// TIDAK menyimpan/mencatat nomor; hanya membentuk tautan wa.me di sisi klien.
function waLinkFromNumber(raw) {
  const digits = String(raw || "").replace(/[^\d]/g, "");
  if (!digits) return null;
  let n = digits;
  if (n.startsWith("0")) n = "62" + n.slice(1);
  else if (n.startsWith("8")) n = "62" + n;
  else if (!n.startsWith("62")) return null;
  if (n.length < 10 || n.length > 15) return null;
  return `https://wa.me/${n}`;
}

// Kompres foto di sisi klien (resize + JPEG) — juga membuang metadata EXIF/GPS via canvas.
async function compressImage(file) {
  try {
    const url = URL.createObjectURL(file);
    const img = document.createElement("img");
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    let { width, height } = img;
    const max = 1600;
    if (Math.max(width, height) > max) { const s = max / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
    const cv = document.createElement("canvas"); cv.width = width; cv.height = height;
    cv.getContext("2d").drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((r) => cv.toBlob(r, "image/jpeg", 0.72));
    if (!blob) return file;
    return new File([blob], (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

export default function HomecareRoomsAdmin() {
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("active");        // "active" | "archived"
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [totalUnread, setTotalUnread] = useState(0);
  const [media, setMedia] = useState({});          // msgId -> objectURL | "loading" | "error"
  const mediaFetching = useRef({});
  const [picker, setPicker] = useState(null);      // "photo" | "video" | null
  const [preview, setPreview] = useState(null);    // {file, url, kind}
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState("");
  const imgCamInput = useRef(null);
  const imgGalInput = useRef(null);
  const vidCamInput = useRef(null);
  const vidGalInput = useRef(null);
  const listScrollRef = useRef(0);
  const pushedRef = useRef(false);
  const activeRef = useRef(null);
  const prevUnreadRef = useRef({});
  const initedRef = useRef(false);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkedRef = useRef(false);

  useEffect(() => { activeRef.current = active?.id || null; }, [active]);

  const loadRooms = useCallback(() => {
    api.get(`/homecare/rooms?status=${tab === "archived" ? "archived" : "active"}`).then((r) => {
      const list = r.data.rooms || [];
      setRooms(list);
      setTotalUnread(r.data.total_unread || 0);
      const cur = {};
      list.forEach((x) => { cur[x.id] = x.unread || 0; });
      if (initedRef.current && tab !== "archived") {
        let inc = null;
        for (const x of list) {
          if (activeRef.current === x.id) continue;               // room aktif dialerting oleh hook, hindari ganda
          if ((cur[x.id] || 0) > (prevUnreadRef.current[x.id] || 0)) inc = x;
        }
        // Suara terpusat di Layout (lonceng). Di sini hanya toast visual, tanpa bunyi ganda.
        if (inc) { toast(`Pesan baru — ${inc.client_name}`); }
      }
      prevUnreadRef.current = cur;
      initedRef.current = true;
    }).catch((e) => toast.error(apiError(e)));
  }, [tab]);
  useEffect(() => { document.title = "Ruang Chat Homecare — GBS"; loadRooms(); const id = setInterval(() => { if (!document.hidden) loadRooms(); }, 10000); return () => clearInterval(id); }, [loadRooms]);
  useEffect(() => { document.title = `${totalUnread > 0 ? `(${totalUnread}) ` : ""}Ruang Chat Homecare — GBS`; }, [totalUnread]);

  const openRoom = useCallback((id) => {
    api.get(`/homecare/rooms/${id}`).then((r) => setActive(r.data.room)).catch((e) => toast.error(apiError(e)));
    api.get(`/homecare/rooms/${id}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {});
    if (!document.hidden) api.post(`/homecare/rooms/${id}/read`).then(() => loadRooms()).catch(() => {});
  }, [loadRooms]);
  useEffect(() => { if (!active) return; const id = setInterval(() => { if (!document.hidden) openRoom(active.id); }, 5000); return () => clearInterval(id); }, [active, openRoom]);

  // Ambil media sebagai blob BER-OTORISASI (JWT admin via api) -> objectURL; TIDAK ada URL publik.
  const fetchMedia = useCallback(async (m, roomId) => {
    if (mediaFetching.current[m.id]) return;
    mediaFetching.current[m.id] = true;
    setMedia((p) => ({ ...p, [m.id]: "loading" }));
    try {
      const r = await api.get(`/homecare/rooms/${roomId}/media/${m.id}`, { responseType: "blob" });
      setMedia((p) => ({ ...p, [m.id]: URL.createObjectURL(r.data) }));
    } catch {
      setMedia((p) => ({ ...p, [m.id]: "error" }));
      mediaFetching.current[m.id] = false;
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    msgs.forEach((m) => {
      if ((m.type === "image" || m.type === "video") && !media[m.id] && !mediaFetching.current[m.id]) fetchMedia(m, active.id);
    });
  }, [msgs, active, media, fetchMedia]);

  // Deep-link: /homecare-chat-admin?room={room_id} auto-opens that room's panel.
  // Invalid/missing id -> stays on the list with a clear toast (no data leak).
  useEffect(() => {
    const rid = sp.get("room");
    if (!rid || deepLinkedRef.current) return;
    deepLinkedRef.current = true;
    api.get(`/homecare/rooms/${rid}`)
      .then((r) => { setActive(r.data.room); openRoom(rid); })
      .catch(() => toast.error("Room chat tidak ditemukan atau sudah ditutup."))
      .finally(() => { const n = new URLSearchParams(sp); n.delete("room"); setSp(n, { replace: true }); });
  }, [sp, setSp, openRoom]);

  const isMineAdmin = useCallback((m) => m.sender_kind === "admin", []);
  useChatAlerts(msgs, isMineAdmin, { silent: true });   // suara ditangani terpusat di Layout (lonceng)

  // Mobile master-detail: entering a room pushes a history entry so hardware/browser Back returns to the list.
  const selectRoom = useCallback((id) => {
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
    if (isMobile && !active) {
      listScrollRef.current = window.scrollY;
      window.history.pushState({ hcRoom: true }, "");
      pushedRef.current = true;
    }
    openRoom(id);
  }, [active, openRoom]);

  useEffect(() => {
    const onPop = () => { pushedRef.current = false; setActive(null); requestAnimationFrame(() => window.scrollTo(0, listScrollRef.current)); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const backToList = useCallback(() => {
    if (pushedRef.current) { window.history.back(); }
    else { setActive(null); requestAnimationFrame(() => window.scrollTo(0, listScrollRef.current)); }
  }, []);

  const reply = async () => {
    const t = text.trim(); if (!t || !active) return;
    try { await api.post(`/homecare/rooms/${active.id}/messages`, { type: "text", text: t }); setText(""); openRoom(active.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- Admin kirim Foto & Video (peserta room; backend validasi jenis/ukuran) ----
  const pick = (which) => {
    const ref = picker === "photo" ? (which === "camera" ? imgCamInput : imgGalInput) : (which === "camera" ? vidCamInput : vidGalInput);
    setPicker(null);
    ref.current?.click();
  };
  const onPick = async (e, kind) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUpErr("");
    if (kind === "image") {
      const f = await compressImage(file);
      setPreview({ file: f, url: URL.createObjectURL(f), kind: "image" });
    } else {
      if (file.size > VID_MAX) return toast.error("Ukuran video melebihi 30 MB. Pilih video lebih pendek.");
      setPreview({ file, url: URL.createObjectURL(file), kind: "video" });
    }
  };
  const cancelPreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); setUpErr(""); };
  const sendMedia = async () => {
    if (!preview || !active) return;
    setUploading(true); setUpErr("");
    try {
      const fd = new FormData(); fd.append("file", preview.file);
      await api.post(`/homecare/rooms/${active.id}/media`, fd);
      cancelPreview(); openRoom(active.id);
    } catch (e) { setUpErr(e?.response?.data?.detail || "Gagal mengunggah. Coba lagi."); }
    finally { setUploading(false); }
  };

  const magicLink = async (id) => {
    try { const r = await api.post(`/homecare/rooms/${id}/client-token`); const url = `${window.location.origin}/homecare-chat/${id}?t=${r.data.client_token}`; await navigator.clipboard?.writeText(url).catch(() => {}); toast.success("Link chat klien disalin. Kirim ke klien via kanal resmi."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resendActivation = async (id) => {
    const pending = active?.activation_pending;
    if (!pending && !window.confirm("Kirim ulang link chat akan MEMBUAT LINK BARU dan MEMBATALKAN link lama. Lanjutkan?")) return;
    try {
      const r = await api.post(`/homecare/rooms/${id}/resend-activation`);
      if (r.data?.status === "pending_activation") toast.error("Konfirmasi pembayaran masih GAGAL — link chat ditahan (pending). Coba lagi nanti.");
      else toast.success(pending ? `Rangkaian notifikasi berhasil dikirim (status: ${r.data.status}).` : `Tautan Buka Chat Homecare baru dikirim via WhatsApp (status: ${r.data.status}). Link lama tidak berlaku.`);
      openRoom(id);
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const act = async (id, path, ok) => { try { await api.post(`/homecare/rooms/${id}/${path}`); toast.success(ok); loadRooms(); if (active?.id === id) openRoom(id); } catch (e) { toast.error(apiError(e)); } };

  // Tombol "WhatsApp Klien" — HANYA admin (halaman ini admin-only). Buka wa.me di tab baru
  // (noopener,noreferrer). TANPA token/link/isi pesan otomatis. Nomor tidak dicatat.
  const openClientWa = () => {
    const url = waLinkFromNumber(active?.contacts?.client_no_whatsapp);
    if (!url) { toast.error("Nomor WhatsApp klien tidak valid."); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const archiveRoom = async (id) => {
    if (!window.confirm("Arsipkan room ini? Room menjadi hanya-baca dan pindah ke tab Arsip. Pesan & media tetap tersimpan untuk audit/komplain.")) return;
    try { await api.post(`/homecare/rooms/${id}/archive`); toast.success("Room diarsipkan."); setActive(null); loadRooms(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitReopen = async () => {
    const reason = reopenReason.trim();
    if (!reason) { toast.error("Alasan wajib diisi."); return; }
    try { await api.post(`/homecare/rooms/${active.id}/reopen-archived`, { reason }); toast.success("Room dibuka kembali."); setShowReopen(false); setReopenReason(""); setActive(null); loadRooms(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const switchTab = (t) => { if (t === tab) return; setActive(null); initedRef.current = false; setTab(t); };

  const renderAdminMedia = (m) => {
    const u = media[m.id];
    if (!u || u === "loading") return <div className="flex h-32 w-40 items-center justify-center rounded-xl bg-black/5 text-slate-400" data-testid={`hc-admin-media-loading-${m.id}`}><CircleNotch size={18} className="animate-spin" /></div>;
    if (u === "error") return (
      <button data-testid={`hc-admin-media-retry-${m.id}`} onClick={() => { mediaFetching.current[m.id] = false; fetchMedia(m, active.id); }} className="flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-xl bg-black/5 text-[11px] font-bold text-[#6A38C7]"><ArrowClockwise size={16} weight="bold" /> Muat ulang</button>
    );
    if (m.type === "image") return <img data-testid={`hc-admin-media-image-${m.id}`} src={u} alt="foto" onClick={() => window.open(u, "_blank")} className="max-h-64 max-w-full cursor-pointer rounded-xl object-cover" />;
    return <video data-testid={`hc-admin-media-video-${m.id}`} src={u} controls playsInline className="max-h-72 max-w-full rounded-xl" />;
  };

  return (
    <div className="mx-auto max-w-5xl p-4" data-testid="homecare-admin">
      <div className="sticky top-0 z-20 -mx-4 mb-2 border-b border-purple-100 bg-white/95 px-4 py-2 backdrop-blur">
        <button data-testid="hc-admin-back-dashboard" onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-xs font-bold text-[#6A38C7] transition-transform active:scale-95">
          ← Kembali ke Dashboard
        </button>
      </div>
      <h1 className="mb-3 flex items-center gap-2 font-heading text-xl font-extrabold text-[#4C1D95]"><ChatCircleDots size={22} weight="duotone" className="text-[#DB2777]" /> Ruang Chat Homecare</h1>
      <div className="mb-3"><ChatSoundBanner /></div>
      <div className="mb-3 flex gap-2" data-testid="hc-admin-tabs">
        <button data-testid="hc-tab-active" onClick={() => switchTab("active")} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tab === "active" ? "bg-[#6A38C7] text-white" : "bg-[#EEE6FF] text-[#6A38C7]"}`}>Aktif</button>
        <button data-testid="hc-tab-archived" onClick={() => switchTab("archived")} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tab === "archived" ? "bg-[#6A38C7] text-white" : "bg-[#EEE6FF] text-[#6A38C7]"}`}>Arsip</button>
      </div>
      <div className="grid gap-4 md:grid-cols-[320px_1fr]">
        <div className={`min-w-0 space-y-2 ${active ? "hidden md:block" : "block"}`} data-testid="hc-admin-list">
          {rooms.length === 0 && <p className="rounded-xl border border-dashed border-pink-200 p-6 text-center text-sm text-slate-400" data-testid="hc-admin-empty">{tab === "archived" ? "Belum ada ruang chat arsip." : "Belum ada ruang chat aktif."}</p>}
          {rooms.map((r) => (
            <button key={r.id} data-testid={`hc-room-${r.id}`} onClick={() => selectRoom(r.id)} className={`w-full rounded-xl border p-3 text-left ${active?.id === r.id ? "border-[#6A38C7] bg-[#F5F0FF]" : "border-purple-100 bg-white"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-slate-700">{r.client_name}</p>
                <div className="flex items-center gap-1">
                  {(r.unread || 0) > 0 && <span data-testid={`hc-unread-${r.id}`} className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{r.unread}</span>}
                  {r.emergency?.active && <span className="flex items-center gap-1 rounded-full bg-[#DB2777] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`hc-badge-emg-${r.id}`}><Warning size={11} weight="fill" /> DARURAT</span>}
                </div>
              </div>
              <p className="text-[11px] text-slate-500">{r.assigned_staff_name} • {SL[r.service_status] || "Menunggu"}</p>
            </button>
          ))}
        </div>

        <div className={`min-w-0 rounded-2xl border border-purple-100 bg-white ${active ? "block" : "hidden md:block"}`} data-testid="hc-admin-detail">
          {!active ? <p className="p-10 text-center text-sm text-slate-400">Pilih ruang chat.</p> : (
            <div className="flex h-[calc(100dvh-9rem)] flex-col md:h-[70vh]">
              <div className="border-b border-purple-100 p-3">
                {/* Baris 1: informasi room SELEBAR PENUH (tidak menyusut oleh tombol) */}
                <div className="flex items-start gap-2">
                  <button data-testid="hc-back-list" onClick={backToList} className="flex shrink-0 items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600 md:hidden"><ArrowLeft size={13} weight="bold" /> Daftar Chat</button>
                  <div className="min-w-0 w-full">
                    <p className="break-words font-bold text-slate-700">{active.client_name} ↔ {active.assigned_staff_name}</p>
                    {active.contacts && <p className="break-words text-[11px] text-slate-500" data-testid="hc-admin-contacts">Kontak operasional — Klien: {active.contacts.client_no_whatsapp || "-"} | Petugas: {active.contacts.staff_no_whatsapp || "-"}</p>}
                    <p className="break-words text-[10px] italic text-slate-400" data-testid="hc-link-note">Kirimkan link ini hanya kepada klien yang bersangkutan.</p>
                    <p className="break-words text-[10px] italic text-slate-400" data-testid="hc-wa-note">Gunakan WhatsApp hanya untuk kebutuhan Admin. Koordinasi layanan tetap melalui Room Chat resmi.</p>
                  </div>
                </div>
                {/* Baris 2: action buttons, wrap rapi */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                <button data-testid="hc-wa-client" onClick={openClientWa} className="flex items-center gap-1 rounded-lg bg-[#25D366]/15 px-2 py-1 text-[11px] font-bold text-[#128C7E]"><WhatsappLogo size={14} weight="fill" /> WhatsApp Klien</button>
                {active.status !== "archived" && <>
                  <button data-testid="hc-magic-link" onClick={() => magicLink(active.id)} className="flex items-center gap-1 rounded-lg bg-[#EEE6FF] px-2 py-1 text-[11px] font-bold text-[#6A38C7]"><LinkSimple size={13} /> Salin Link Chat Klien</button>
                  {active.activation_pending && <span data-testid="hc-activation-pending" className="flex items-center gap-1 rounded-lg bg-rose-100 px-2 py-1 text-[11px] font-bold text-rose-700">WA aktivasi tertunda (konfirmasi pembayaran gagal/tertahan)</span>}
                  <button data-testid="hc-resend-activation" onClick={() => resendActivation(active.id)} className="flex items-center gap-1 rounded-lg bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-700"><ArrowClockwise size={13} /> Kirim Ulang Link Chat</button>
                  <button data-testid="hc-revoke" onClick={() => act(active.id, "revoke-client-token", "Token klien dicabut")} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600"><Prohibit size={13} /> Cabut</button>
                  <button data-testid="hc-reopen" onClick={() => act(active.id, "reopen", "Ruang dibuka kembali")} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600"><ArrowClockwise size={13} /> Buka Ulang</button>
                  {active.archivable && <button data-testid="hc-archive" onClick={() => archiveRoom(active.id)} className="flex items-center gap-1 rounded-lg bg-slate-200 px-2 py-1 text-[11px] font-bold text-slate-700"><Archive size={13} weight="bold" /> Arsipkan Room</button>}
                  {active.emergency?.active && <button data-testid="hc-clear-emg" onClick={() => act(active.id, "emergency/clear", "Darurat dibersihkan")} className="rounded-lg bg-[#DB2777] px-2 py-1 text-[11px] font-bold text-white">Selesai Darurat</button>}
                </>}
                {active.status === "archived" && <button data-testid="hc-reopen-archived" onClick={() => { setReopenReason(""); setShowReopen(true); }} className="flex items-center gap-1 rounded-lg bg-[#EEE6FF] px-2 py-1 text-[11px] font-bold text-[#6A38C7]"><ArrowUUpLeft size={13} weight="bold" /> Buka Kembali</button>}
                </div>
              </div>
              {active.status === "archived" && <div data-testid="hc-admin-archived-banner" className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-center text-[12px] font-semibold text-amber-800">Layanan telah selesai. Room Chat ini telah diarsipkan dan hanya dapat dibaca.</div>}
              <div className="min-w-0 flex-1 space-y-2 overflow-y-auto p-3" data-testid="hc-admin-messages">
                {msgs.map((m) => {
                  if (m.sender_kind === "system") return <div key={m.id} className="mx-auto rounded-full bg-slate-100 px-3 py-1 text-center text-[11px] text-slate-500">{m.text}</div>;
                  const mine = m.sender_kind === "admin";
                  const isMedia = m.type === "image" || m.type === "video";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] min-w-0 break-words rounded-2xl px-3 py-2 text-base ${mine ? "bg-[#6A38C7] text-white" : "border border-purple-100 bg-white text-slate-700"} ${isMedia ? "p-1.5" : ""}`}>
                        <p className={`mb-0.5 text-[10px] font-bold opacity-70 ${isMedia ? "px-1.5 pt-1" : ""}`}>{m.sender_name}</p>
                        {m.type === "location"
                          ? <a data-testid="hc-admin-open-location" className={`mt-0.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold ${mine ? "bg-white/20 text-white" : "bg-[#EEE6FF] text-[#6A38C7]"}`} target="_blank" rel="noreferrer" href={m.location?.url || `https://maps.google.com/?q=${m.location?.lat},${m.location?.lng}`}>📍 Buka Lokasi</a>
                          : isMedia ? renderAdminMedia(m) : m.text}
                        <p className={`mt-0.5 text-[9px] opacity-60 ${isMedia ? "px-1.5 pb-0.5" : ""}`}>{(m.created_at_wita || "").slice(11, 16)} WITA</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {active.status === "open" ? (<>
              <input ref={imgGalInput} type="file" accept="image/*" hidden onChange={(e) => onPick(e, "image")} data-testid="hc-admin-input-photo" />
              <input ref={imgCamInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onPick(e, "image")} data-testid="hc-admin-input-photo-camera" />
              <input ref={vidGalInput} type="file" accept="video/*" hidden onChange={(e) => onPick(e, "video")} data-testid="hc-admin-input-video" />
              <input ref={vidCamInput} type="file" accept="video/*" capture="environment" hidden onChange={(e) => onPick(e, "video")} data-testid="hc-admin-input-video-camera" />
              <div className="flex items-center gap-1.5 border-t border-purple-100 p-2">
                <button data-testid="hc-admin-attach-photo" onClick={() => setPicker("photo")} className="rounded-full bg-[#EEE6FF] p-2 text-[#6A38C7]"><Camera size={18} weight="fill" /></button>
                <button data-testid="hc-admin-attach-video" onClick={() => setPicker("video")} className="rounded-full bg-[#EEE6FF] p-2 text-[#6A38C7]"><VideoCamera size={18} weight="fill" /></button>
                <input data-testid="hc-admin-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()} placeholder="Balas sebagai Admin GBS…" className="min-w-0 flex-1 rounded-full border border-purple-100 bg-[#FDFBFE] px-3 py-2 text-base outline-none" />
                <button data-testid="hc-admin-send" onClick={reply} className="rounded-full bg-[#6A38C7] p-2 text-white"><PaperPlaneRight size={18} weight="fill" /></button>
              </div>
              </>) : (
                <div data-testid="hc-admin-readonly" className="border-t border-purple-100 bg-slate-50 px-3 py-3 text-center text-xs font-semibold text-slate-500">{active.status === "archived" ? "Room arsip — hanya baca." : "Ruang chat sudah ditutup — hanya baca."}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {picker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setPicker(null)} data-testid="hc-admin-media-picker">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-4 pb-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="mb-3 text-center text-sm font-extrabold text-[#4C1D95]">{picker === "photo" ? "Foto" : "Video"}</p>
            <div className="space-y-2">
              <button data-testid={picker === "photo" ? "hc-admin-pick-photo-camera" : "hc-admin-pick-video-camera"} onClick={() => pick("camera")}
                className="flex w-full items-center gap-3 rounded-2xl bg-[#EEE6FF] px-4 py-3 text-sm font-bold text-[#6A38C7] transition-transform active:scale-[.99]">
                {picker === "photo" ? <Camera size={20} weight="fill" /> : <VideoCamera size={20} weight="fill" />}
                Ambil {picker === "photo" ? "Foto" : "Video"}
              </button>
              <button data-testid={picker === "photo" ? "hc-admin-pick-photo-gallery" : "hc-admin-pick-video-gallery"} onClick={() => pick("gallery")}
                className="flex w-full items-center gap-3 rounded-2xl bg-[#EEE6FF] px-4 py-3 text-sm font-bold text-[#6A38C7] transition-transform active:scale-[.99]">
                <Images size={20} weight="fill" /> Pilih dari Galeri
              </button>
              <button data-testid="hc-admin-pick-cancel" onClick={() => setPicker(null)}
                className="w-full rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-500">Batal</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4" data-testid="hc-admin-media-preview">
          <div className="flex items-center justify-between text-white">
            <p className="text-sm font-bold">Pratinjau {preview.kind === "image" ? "Foto" : "Video"}</p>
            <button data-testid="hc-admin-media-cancel" onClick={cancelPreview} disabled={uploading} className="rounded-full bg-white/15 p-1.5 disabled:opacity-40"><X size={18} weight="bold" /></button>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-hidden py-4">
            {preview.kind === "image"
              ? <img src={preview.url} alt="pratinjau" className="max-h-full max-w-full rounded-xl object-contain" />
              : <video src={preview.url} controls playsInline className="max-h-full max-w-full rounded-xl" />}
          </div>
          {upErr && <p className="mb-2 text-center text-[12px] font-semibold text-pink-300" data-testid="hc-admin-media-uperr">{upErr}</p>}
          <button data-testid="hc-admin-media-send" onClick={sendMedia} disabled={uploading} className="flex items-center justify-center gap-2 rounded-full bg-[#6A38C7] py-3 text-sm font-bold text-white disabled:opacity-60">
            {uploading ? <><CircleNotch size={18} className="animate-spin" /> Mengunggah…</> : <>{upErr ? "Coba Kirim Ulang" : "Kirim"}</>}
          </button>
        </div>
      )}

      {showReopen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowReopen(false)} data-testid="hc-reopen-dialog">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="mb-1 font-heading text-base font-extrabold text-[#4C1D95]">Buka Kembali Room Arsip</p>
            <p className="mb-3 text-xs text-slate-500">Alasan wajib diisi dan akan tercatat pada audit log.</p>
            <textarea data-testid="hc-reopen-reason" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={3} placeholder="Contoh: klien mengajukan komplain lanjutan…" className="w-full rounded-xl border border-purple-100 bg-[#FDFBFE] px-3 py-2 text-sm outline-none" />
            <div className="mt-3 flex justify-end gap-2">
              <button data-testid="hc-reopen-cancel" onClick={() => setShowReopen(false)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-500">Batal</button>
              <button data-testid="hc-reopen-submit" onClick={submitReopen} className="rounded-xl bg-[#6A38C7] px-4 py-2 text-sm font-bold text-white">Buka Kembali</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
